import { useEffect, useState } from "react";
import { Loader2, ShieldCheck, Plus } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

// Single source of truth for what shows in the permissions matrix.
// `viewOnly: true` means only the "View" action is offered.
export const PERMISSION_MODULES = [
  { key: "member", label: "Member" },
  { key: "master_data", label: "Master Data" },
  { key: "fuel_access", label: "Fuel Access" },
  { key: "blank_fuel_access", label: "Blank Fuel Access" },
  { key: "scan_history", label: "Scan History", viewOnly: true },
  {
    key: "report_master_vehicle_registration",
    label: "Master Vehicle Registration Report",
    viewOnly: true,
  },
  {
    key: "report_master_scan",
    label: "Master Scan Report",
    viewOnly: true,
  },
  { key: "report_vehicles_by_user", label: "Vehicles by User", viewOnly: true },
  { key: "report_vehicles_by_bunk", label: "Vehicles by Bunk", viewOnly: true },
  { key: "report_vehicles_by_company", label: "Vehicles by Company", viewOnly: true },
  { key: "report_vehicles_by_city", label: "Vehicles by City", viewOnly: true },
  { key: "report_vehicles_by_district", label: "Vehicles by District", viewOnly: true },
  { key: "report_vehicles_by_state", label: "Vehicles by State", viewOnly: true },
  { key: "report_vehicles_by_country", label: "Vehicles by Country", viewOnly: true },
  { key: "report_scan_by_bunk", label: "Scan by Bunk", viewOnly: true },
  { key: "report_scan_by_company", label: "Scan by Company", viewOnly: true },
  { key: "report_scan_by_city", label: "Scan by City", viewOnly: true },
  { key: "report_scan_by_district", label: "Scan by District", viewOnly: true },
  { key: "report_scan_by_state", label: "Scan by State", viewOnly: true },
  { key: "report_scan_by_country", label: "Scan by Country", viewOnly: true },
];

const ACTIONS = ["view", "add", "edit", "delete"];

function emptyPermissions() {
  const out = {};
  for (const m of PERMISSION_MODULES) {
    out[m.key] = { view: false, add: false, edit: false, delete: false };
  }
  return out;
}

function RoleFormDialog({ open, onOpenChange, onSaved }) {
  const [name, setName] = useState("");
  const [permissions, setPermissions] = useState(emptyPermissions());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName("");
      setPermissions(emptyPermissions());
    }
  }, [open]);

  const setPerm = (modKey, action, checked) => {
    setPermissions((prev) => ({
      ...prev,
      [modKey]: { ...prev[modKey], [action]: !!checked },
    }));
  };

  const toggleRowAll = (mod, checked) => {
    setPermissions((prev) => {
      if (mod.viewOnly) {
        return { ...prev, [mod.key]: { ...prev[mod.key], view: !!checked } };
      }
      return {
        ...prev,
        [mod.key]: {
          view: !!checked,
          add: !!checked,
          edit: !!checked,
          delete: !!checked,
        },
      };
    });
  };

  const rowAllChecked = (mod) => {
    const p = permissions[mod.key] || {};
    if (mod.viewOnly) return !!p.view;
    return ACTIONS.every((a) => p[a]);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Role name is required.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/roles", {
        name: name.trim(),
        permissions,
      });
      toast.success(`Role "${data.name}" created`);
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) || "Failed to create role"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-white border-black/5 max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="role-form-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-[#0A0A0A]">Create Role</DialogTitle>
          <DialogDescription className="text-[#525252]">
            Pick a name and choose what this role can do on each module.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 overflow-y-auto pr-1">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-1.5 block">
              Role Name <span className="text-[#F50E02]">*</span>
            </Label>
            <Input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Bunk Manager, Dispatcher, Auditor"
              className="h-10 bg-white border-black/10"
              data-testid="role-name-input"
            />
          </div>

          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-2 block">
              Permissions
            </Label>
            <div className="border border-black/5 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="permissions-matrix">
                  <thead>
                    <tr className="bg-[#FAFAFA] border-b border-black/5 text-left">
                      <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-[#525252] w-1/2">
                        Module
                      </th>
                      <th className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-[#525252] text-center">
                        All
                      </th>
                      {ACTIONS.map((a) => (
                        <th
                          key={a}
                          className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-[#525252] text-center"
                        >
                          {a}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {PERMISSION_MODULES.map((m) => {
                      const p = permissions[m.key] || {};
                      return (
                        <tr
                          key={m.key}
                          className="border-b border-black/5 last:border-0"
                        >
                          <td className="px-4 py-2.5 text-[#0A0A0A] font-medium">
                            {m.label}
                            {m.viewOnly ? (
                              <span className="ml-2 text-[10px] font-normal uppercase tracking-wider text-[#A3A3A3]">
                                view only
                              </span>
                            ) : null}
                          </td>
                          <td className="px-3 py-2.5 text-center">
                            <Checkbox
                              checked={rowAllChecked(m)}
                              onCheckedChange={(c) => toggleRowAll(m, c)}
                              data-testid={`perm-${m.key}-all`}
                            />
                          </td>
                          {ACTIONS.map((a) => {
                            const disabled = m.viewOnly && a !== "view";
                            return (
                              <td
                                key={a}
                                className="px-3 py-2.5 text-center"
                              >
                                <Checkbox
                                  checked={!!p[a]}
                                  disabled={disabled}
                                  onCheckedChange={(c) =>
                                    setPerm(m.key, a, c)
                                  }
                                  data-testid={`perm-${m.key}-${a}`}
                                />
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="pt-4 border-t border-black/5 mt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="border-black/10"
            data-testid="role-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#F50E02] hover:bg-[#D80C02] text-white"
            data-testid="role-save"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
            Create Role
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Roles() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const loadRoles = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/roles");
      setItems(data || []);
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load roles"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoles();
  }, []);

  return (
    <div data-testid="roles-page">
      <header className="bg-white border-b border-black/5 px-8 py-5 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-[#0A0A0A] tracking-tight">
            Role & Permission
          </h1>
          <p className="text-xs text-[#525252] mt-0.5">
            {items.length} {items.length === 1 ? "role" : "roles"} total.
          </p>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          className="bg-[#F50E02] hover:bg-[#D80C02] text-white h-10"
          data-testid="add-role-button"
        >
          <Plus className="h-4 w-4 mr-1.5" />
          Create Role
        </Button>
      </header>

      <div className="p-8">
        <div className="bg-white border border-black/5 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="roles-table">
              <thead>
                <tr className="bg-[#FAFAFA] border-b border-black/5 text-left">
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">
                    Sr.No.
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">
                    Role Name
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">
                    Access
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-10 text-center text-[#525252]">
                      <Loader2 className="h-5 w-5 animate-spin inline-block text-[#F50E02]" />
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-10 text-center text-[#525252]">
                      <div className="inline-flex flex-col items-center gap-2">
                        <ShieldCheck className="h-6 w-6 text-[#A3A3A3]" />
                        <span>No roles found.</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  items.map((r, idx) => (
                    <tr
                      key={r.id}
                      className="border-b border-black/5 last:border-0 hover:bg-[#FAFAFA]/60"
                      data-testid={`role-row-${r.id}`}
                    >
                      <td className="px-4 py-3 text-[#525252]">{idx + 1}</td>
                      <td className="px-4 py-3 font-medium text-[#0A0A0A]">{r.name}</td>
                      <td className="px-4 py-3 text-[#A3A3A3]">—</td>
                      <td className="px-4 py-3 text-[#525252]">
                        {formatDate(r.created_at)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <RoleFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSaved={() => loadRoles()}
      />
    </div>
  );
}
